/**
 * UNVALIDATED INTERPRETATION LOGIC — single source of truth.
 *
 * No fake numbers are shown to the user: every score/metric/severity/% is
 * computed from real API data by the panel builders. What lives here is the
 * *interpretation* layer — score→label/colour band cutoffs — whose exact
 * thresholds are not yet clinically/product signed off. Replace the cutoffs
 * here when validated; panels read everything through the VM service, so
 * nothing else changes.
 *
 * Every threshold below is a tracked open issue. See `OPEN_ISSUES.md` in this
 * folder (OI-1..OI-13) for the exact item, location and what closes it.
 */

import { Band, Tone } from './results-vm';

/** Tag pill colours used across the design. */
export const TAG_COLORS: Record<Tone, { bg: string; color: string }> = {
  green: { bg: '#e8f7ef', color: '#1a7a4a' },
  teal: { bg: '#e8f7ef', color: '#1a7a4a' },
  amber: { bg: '#fff4e0', color: '#9A6400' },
  // wordfirst sec 3/6 (03.09): the top tier is DEEP amber (same hue 36, darker),
  // NOT a third colour and NOT red/coral — fill --status-amber-deep, chip bg
  // --amber-deep-pill #f9e6c2, chip text --amber-deep-ink #8A5600.
  amberDeep: { bg: '#f9e6c2', color: '#8A5600' },
  red: { bg: '#fff0e6', color: '#c44a00' },
  neutral: { bg: '#f3f1ee', color: '#6d6d6d' },
};

/** Vibrant stroke colour for the score-ring fill arc, per tone. */
export const RING_COLORS: Record<Tone, string> = {
  green: '#34C759',
  teal: '#34C759',
  amber: '#FF9F0A',
  amberDeep: '#C47A08',
  red: '#e05a6e',
  neutral: '#c8c8cc',
};

function band(tone: Tone, labelKey: string): Band {
  return {
    tone,
    labelKey,
    tagBg: TAG_COLORS[tone].bg,
    tagColor: TAG_COLORS[tone].color,
    ringColor: RING_COLORS[tone],
  };
}

/**
 * wordfirst sec 3 evaluation dictionary. Score->word thresholds live HERE in one
 * config object (BAND_CONFIG) instead of scattered magic numbers, so recalibrating
 * a domain is a single data edit. The word is DERIVED from the stored numeric score
 * at render time and never stored, so new cutoffs apply to ALL scans incl. old ones
 * with no migration. "lower = better" for imprint + symptoms; "higher = better" for
 * think + sense.
 *
 * NOTE: these are the CURRENT cutoffs (unchanged behaviour, OI-1/OI-2/OI-3). The
 * spec's sec-3 OPENING VALUES (imprint/symptoms 34/67, sense 70/50) + two-colour
 * palette get applied with the word-first domain panel (WF-DOMAIN-PANEL), together
 * with the hero-copy thresholds that mirror these, so band + hero copy + 3-level bar
 * recalibrate in sync. Recalibration on the first real distribution is Danna's call.
 */
export type BandDir = 'lowerBetter' | 'higherBetter';
export interface BandLevel {
  /** upper bound (exclusive) for lowerBetter, lower bound (inclusive) for higherBetter; omit on the catch-all worst level */
  bound?: number;
  tone: Tone;
  labelKey: string;
}
export interface DomainBandConfig {
  dir: BandDir;
  /** ordered best -> worst */
  levels: BandLevel[];
}

// wordfirst sec 3 OPENING VALUES (applied with WF-DOMAIN-PANEL): imprint/symptoms
// 0-33 / 34-66 / 67-100 (bounds 34 + 67); think/sense 70-100 / 50-69 / 0-49. These
// live here as config, not code, so recalibrating on the first real distribution is
// a single data edit (Danna's call, spec sec 3). THREE WORDS, and per the 03.09
// colour update: best = green, middle = amber, worst = DEEP amber (amberDeep, same
// hue 36 as amber, only darker). The deep amber is NOT a third colour and NOT
// red/coral/warning-orange (a reddish tone on "pronounced signals" reads as a
// medical/regulatory claim, forbidden); it exists only because worst + middle came
// out identical when both were amber. Word + position + shade all now separate them.
export const BAND_CONFIG: Record<'imprint' | 'symptoms' | 'think' | 'sense', DomainBandConfig> = {
  imprint: {
    dir: 'lowerBetter',
    levels: [
      { bound: 34, tone: 'green', labelKey: 'results.bands.mildSignals' },
      { bound: 67, tone: 'amber', labelKey: 'results.bands.moderateSignals' },
      { tone: 'amberDeep', labelKey: 'results.bands.pronouncedSignals' },
    ],
  },
  symptoms: {
    dir: 'lowerBetter',
    levels: [
      { bound: 34, tone: 'green', labelKey: 'results.bands.lightLoad' },
      { bound: 67, tone: 'amber', labelKey: 'results.bands.mediumLoad' },
      { tone: 'amberDeep', labelKey: 'results.bands.heavyLoad' },
    ],
  },
  think: {
    dir: 'higherBetter',
    levels: [
      { bound: 70, tone: 'green', labelKey: 'results.bands.strongBase' },
      { bound: 50, tone: 'amber', labelKey: 'results.bands.moderateBase' },
      { tone: 'amberDeep', labelKey: 'results.bands.lowBase' },
    ],
  },
  // SENSE gets its own word set (wordfirst sec 3). Same higher-is-better principle
  // as THINK. Wired into the SENSE panel only once its composite/status validity is
  // unblocked (measurement package); the consumer SENSE panel stays as-is for now.
  sense: {
    dir: 'higherBetter',
    levels: [
      { bound: 70, tone: 'green', labelKey: 'results.bands.mostHigh' },
      { bound: 50, tone: 'amber', labelKey: 'results.bands.mixedResults' },
      { tone: 'amberDeep', labelKey: 'results.bands.worthTracking' },
    ],
  },
};

function bandFromConfig(cfg: DomainBandConfig, score: number): Band {
  for (const lvl of cfg.levels) {
    if (lvl.bound == null) break; // catch-all worst level
    if (cfg.dir === 'lowerBetter' ? score < lvl.bound : score >= lvl.bound) {
      return band(lvl.tone, lvl.labelKey);
    }
  }
  const worst = cfg.levels[cfg.levels.length - 1];
  return band(worst.tone, worst.labelKey);
}

export function imprintBand(score: number): Band { return bandFromConfig(BAND_CONFIG.imprint, score); }
export function symptomBand(score: number): Band { return bandFromConfig(BAND_CONFIG.symptoms, score); }
export function higherBetterBand(score: number): Band { return bandFromConfig(BAND_CONFIG.think, score); }
export function senseBand(score: number): Band { return bandFromConfig(BAND_CONFIG.sense, score); }

/**
 * wordfirst sec 2: the 3-level bar model. One segment/caption per config level, in
 * DOM order best -> worst (in an RTL page the best therefore renders on the RIGHT,
 * which is the spec's fixed rule regardless of whether the number behind it is high
 * or low). Exactly one level is `on` (the one the score falls in). `scaleColor` is
 * green for the best level, `deep` (deep amber) for the worst level, amber for the
 * middle — worst reads distinct from middle without a warning colour (03.09).
 */
export interface ScaleLevel {
  labelKey: string;
  on: boolean;
  scaleColor: 'green' | 'amber' | 'deep';
}
function activeLevelIndex(cfg: DomainBandConfig, score: number): number {
  for (let i = 0; i < cfg.levels.length; i++) {
    const b = cfg.levels[i].bound;
    if (b == null) return i; // catch-all worst level
    if (cfg.dir === 'lowerBetter' ? score < b : score >= b) return i;
  }
  return cfg.levels.length - 1;
}
export function bandScale(cfg: DomainBandConfig, score: number): ScaleLevel[] {
  const active = activeLevelIndex(cfg, score);
  return cfg.levels.map((lvl, i) => ({
    labelKey: lvl.labelKey,
    on: i === active,
    scaleColor: lvl.tone === 'green' ? 'green' : lvl.tone === 'amberDeep' ? 'deep' : 'amber',
  }));
}

/**
 * Cohort averages — computed at runtime by the server cohort endpoint from
 * existing scans (NOT hardcoded; no stubs — OPEN_ISSUES.md OI-12). `null`
 * fields mean "no real average available yet" → the UI hides that comparison.
 * The imprint average is always null/omitted (blocked until the imprint model
 * is locked — OI-13).
 */
export interface CohortAverages {
  /** true when returned average is for the user's specific age band (N≥30) */
  ageQualified: boolean;
  vitality: number | null;
  skinMetric: { pores: number; texture: number; hydration: number; pigmentation: number } | null;
  symptomLoad: number | null;
  cognitive: number | null;
  imprint: number | null;
  cognitiveLens: { speed: number; memory: number; attention: number } | null;
}

/** Minimum cohort sample to show an age-banded average (OPEN_ISSUES.md OI-12). */
export const MIN_BAND_N = 30;

/** Age → cohort band label. Only bands with N≥30 may show the age qualifier. */
export function ageBand(age: number | null | undefined): { key: string; ageQualified: boolean } {
  if (age == null) return { key: 'all', ageQualified: false };
  // Per OI-12: skin qualifies <40 / 40–44 / 45–49 only.
  if (age < 40) return { key: 'lt40', ageQualified: true };
  if (age < 45) return { key: '40_44', ageQualified: true };
  if (age < 50) return { key: '45_49', ageQualified: true };
  return { key: 'all', ageQualified: false }; // 50–54 / 55+ → fallback all-sample
}
